import { useState, useEffect, useContext } from "react";
import { BiX } from "react-icons/bi";
import DataTable from "../../Table";
import LoadingSpinner from "../../Loader/load";
import { ClientContext } from "../../../contexts/clientProvider";
import { ItensContext } from "../../../contexts/ItensProvider"; // Supondo que exista um contexto para itens
import { ContratosContext } from "../../../contexts/ContratosProvider";
import SeletorContrato from "../SeletorContrato";
import BaseButton from "../../General/FormsComp/Button/BaseButton";
import apiUser from "../../../apiUser";
import { MESES, fmtBRL } from "../../Faturamento/format";
import { rotuloStatusContrato } from "../Visualizar/Types/contratoStatus";

// Normaliza para comparar sem acento/caixa (ex.: "Única" -> "unica").
const normalize = (txt) => (txt || '').toString().normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const Itens = ({ cliente, closeModal }) => {
    const { clientes } = useContext(ClientContext);
    const { getItensByClient, itens, loading, setItens, updateValorImplantada, salvarAjusteEquip, removerAjusteEquip } = useContext(ItensContext);
    const { contratos, clienteDosContratos, getContratosByClient } = useContext(ContratosContext);
    const [clienteSelecionado, setClienteSelecionado] = useState(null);
    const [contratoSelecionado, setContratoSelecionado] = useState(null);
    const [editedValue, setEditedValue] = useState(null);
    // Default = competencia do disparo (mes anterior). Lazy initializer: roda so na montagem.
    const [competencia, setCompetencia] = useState(() => {
        const d = new Date();
        d.setDate(1);
        d.setMonth(d.getMonth() - 1);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
    });
    const month = Number(competencia.slice(5, 7)); // mes derivado p/ o filtro de itens 'unica'
    const [totalMonth, setTotalMonth] = useState(0);
    const [total, setTotal] = useState(0);
    const [uniqueTotal, setUniqueTotal] = useState(0);

    // Nome do mês a partir do número (1-12), reusando MESES do módulo de Faturamento.
    const getMonthName = (monthId) => MESES[Number(monthId) - 1] || '';

    useEffect(() => {
        getData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cliente, competencia]);

    useEffect(() => {
        getContratosByClient(cliente);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cliente]);

    // A lista global só vale quando pertence a ESTE cliente — o provider é
    // compartilhado e pode ainda carregar a lista do cliente anterior.
    const contratosProntos = Number(clienteDosContratos) === Number(cliente);

    // Auto-skip: 1 contrato só → seleciona sozinho (decisão do spec).
    useEffect(() => {
        if (contratosProntos && !contratoSelecionado && contratos.length === 1) setContratoSelecionado(contratos[0]);
    }, [contratosProntos, contratos, contratoSelecionado]);

    // Fonte única filtrada pelo contrato selecionado: usar em todos os pontos
    // que hoje exibem/calculam a partir de `itens`.
    const itensDoContrato = contratoSelecionado ? itens.filter((i) => i.id_contrato === contratoSelecionado.id) : [];

    useEffect(() => {
        calculateTotals()
    }, [itens, month, contratoSelecionado]);



    const getData = async () => {
        const client = clientes.find((client) => client.id === cliente);
        setClienteSelecionado(client);
        getItensByClient(client.id, competencia);
        calculate();
    }

    const [sincronizandoSpxo, setSincronizandoSpxo] = useState(false);
    // Sync SPXO só DESTE cliente (rápido: só os circuitos do CNPJ dele).
    // Roda em background no servidor; faz poll do status e recarrega os itens.
    const sincronizarSpxo = async () => {
        setSincronizandoSpxo(true);
        try {
            const { data } = await apiUser.post('/faturamento/spxo/sincronizar', { competencia, cliente });
            if (!data.success) { alert(data.message || 'Falha ao iniciar a sincronização.'); return; }
            for (let i = 0; i < 30; i++) {
                await new Promise((r) => setTimeout(r, 3000));
                const { data: st } = await apiUser.get('/faturamento/spxo/status');
                if (st.success && !st.sync_em_andamento) { getData(); return; }
            }
            alert('A sincronização ainda está rodando — recarregue os itens em instantes.');
        } catch {
            alert('Falha ao sincronizar com o SPXO.');
        } finally { setSincronizandoSpxo(false); }
    }
    const editValue = (e, item, key) => {
        let value = e.target.value;
        if (isNaN(value)) {
            return;
        }
        setEditedValue({ ...item, [key]: e.target.value });
        let newItens = itens.map((i) => {
            if (i.id === item.id) {
                let newValue = parseFloat(e.target.value);
                i.valor_mes = parseFloat(i.valor_unitario) * newValue;
                return { ...i, [key]: e.target.value };
            }

            return i;
        });
        setItens([...newItens]);

    }
    const saveEdit = async () => {
        if (!editedValue) return;
        if (editedValue.tipo === "Equipamento") {
            const ok = await salvarAjusteEquip({
                id_cliente: cliente, competencia,
                id_item: editedValue.id, quantidade: Number(editedValue.quantidade_implantada),
            });
            if (ok) { getItensByClient(cliente, competencia); setEditedValue(null); }
            // falha: mantem editedValue para o usuario tentar de novo (alerta ja exibido pelo provider)
        } else {
            // Auditada com motivo opcional (09/out/2026); Cancelar desiste da edição.
            const motivo = window.prompt('Motivo da alteração da quantidade implantada (opcional):', '');
            if (motivo === null) {
                setEditedValue(null);
                getItensByClient(cliente, competencia);
                return;
            }
            updateValorImplantada({ ...editedValue, motivo: motivo.trim() });
            setEditedValue(null);
        }
    }

    const resetAjuste = async (item) => {
        const ok = await removerAjusteEquip({ id_cliente: cliente, competencia, id_item: item.id });
        if (ok) getItensByClient(cliente, competencia);
    }

    // Origem pela fonte da quantidade (patrimônio/SPXO/manual).
    const origemFonte = (item) => item.fonte_quantidade === 'spxo'
                ? (item.ajustado
                    ? (
                        <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">Ajustado (SPXO {item.quantidade_auto ?? 0})</span>
                            <button type="button" onClick={() => resetAjuste(item)} className="text-xs font-semibold px-2 py-0.5 rounded-md border border-vox-blue-600 text-vox-blue-700 hover:bg-vox-blue-50 dark:text-vox-blue-200 dark:border-vox-blue-300 dark:hover:bg-gray-700">voltar ao automático</button>
                        </div>
                    )
                    : <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">SPXO ({item.quantidade_auto ?? 0})</span>)
                : item.tipo !== "Equipamento"
                ? <span className="text-gray-400">—</span>
                : item.ajustado
                    ? (
                        <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">Ajustado</span>
                            <button type="button" onClick={() => resetAjuste(item)} className="text-xs font-semibold px-2 py-0.5 rounded-md border border-vox-blue-600 text-vox-blue-700 hover:bg-vox-blue-50 dark:text-vox-blue-200 dark:border-vox-blue-300 dark:hover:bg-gray-700">voltar ao automático</button>
                        </div>
                    )
                    : <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">Automático ({item.quantidade_auto ?? 0})</span>;

    const columns = [
        { label: "Descrição", key: "descricao", wrapText: true, width: "w-80" },
        { label: "Tipo", key: "tipo", width: "w-24" },
        { label: "Qtd. Contratada", key: "quantidade_contratada", width: "w-32" },
        {
            label: "Qtd. Implantada",
            key: "quantidade_implantada",
            editable: editValue,
            saveEdit: saveEdit,
            width: "w-32"
        },
        {
            label: "Origem", key: "origem", width: "w-48",
            render: (item) => (
                <div className="flex flex-col gap-1">
                    {origemFonte(item)}
                    {/* Licença vinculada / teto no contratado (spec 2026-10-09) */}
                    {item.quantidade_vinculo !== undefined && (
                        <span title={item.motivo || ''} className="px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800 self-start">
                            Vinculada (digitada {item.quantidade_digitada ?? 0})
                        </span>
                    )}
                    {Number(item.excedente) > 0 && (
                        <span title={item.motivo || ''} className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 self-start">
                            Acima do contratado (+{item.excedente})
                        </span>
                    )}
                </div>
            ),
        },
        { label: "Grupo", key: "grupo_nome", wrapText: true, width: "w-40" },
        { label: "Unidade", key: "unidade", width: "w-20" },
        { label: "Valor Unitário", key: "valor_unitario", type: "money", width: "w-32" },
        { label: "Valor Implantado", key: "valor_mes", type: "money", width: "w-32" },
        { label: "Mensal Contratado", key: "valor_total", type: "money", width: "w-40" },
    ];
    const verifyClick = (e, closeModal) => {
        if (e.target === e.currentTarget) closeModal();
    }

    const calculate = () => {
        let newItens = itens.length > 0 ? [...itens] : [];
        newItens = newItens.map((item) => {
            item.valor_mes = parseFloat(parseFloat(item.valor_unitario) * parseFloat(item.quantidade_implantada))
            return item;
        });
        setItens([...newItens]);
        calculateTotals()
    }
    const calculateTotals = () => {
        const monthlyItems = (itensDoContrato && itensDoContrato.length > 0) ? itensDoContrato.filter(item => normalize(item.unidade) !== 'unica') : [];
        const uniqueItemsForMonth = (itensDoContrato && itensDoContrato.length > 0) ? itensDoContrato.filter(item => {
            return normalize(item.unidade) === 'unica' && Number(item.quantidade_contratada) === Number(month);
        }) : [];

        const newTotalMonth = monthlyItems.reduce((acc, item) => {
            const valorMes = parseFloat(item.valor_mes ?? 0);
            return acc + (isNaN(valorMes) ? 0 : valorMes);
        }, 0);

        const newTotal = monthlyItems.reduce((acc, item) => {
            const valorTotal = parseFloat(item.valor_total ?? 0);
            return acc + (isNaN(valorTotal) ? 0 : valorTotal);
        }, 0);

        const newUniqueTotal = uniqueItemsForMonth.reduce((acc, item) => {
            const valorCobranca = parseFloat(parseFloat(item.valor_unitario)) * parseFloat(item.quantidade_implantada);
            return acc + (isNaN(valorCobranca) ? 0 : valorCobranca);
        }, 0);
        setTotalMonth(newTotalMonth.toFixed(2));
        setTotal(newTotal.toFixed(2));
        setUniqueTotal(newUniqueTotal.toFixed(2));
    }

    const buildTableData = () => {
        const filtered = (itensDoContrato || []).filter(item => {
            if (normalize(item.unidade) === 'unica') {
                return Number(item.quantidade_contratada) === Number(month);
            }
            return true;
        });

        // Traduz quantidade_contratada para o nome do mês quando for "Única"
        const translated = filtered.map(item => {
            if (normalize(item.unidade) === 'unica') {
                return {
                    ...item,
                    quantidade_contratada: getMonthName(item.quantidade_contratada)
                };
            }
            return item;
        });

        return translated;
    }

    const generateFooterValues = () => {
        let totalImplantado = `Total Implantado: ${fmtBRL(totalMonth)}`
        let totalContratado = `Total Contratado: ${fmtBRL(total)}`
        let cobrancaUnica;
        if (Number(uniqueTotal) > 0) {
            cobrancaUnica = `Cobrança Única: ${fmtBRL(uniqueTotal)}`
        }
        const values = [
            cobrancaUnica,
            totalImplantado,
            totalContratado,
        ].filter((value) => value !== undefined && value !== '');

        return values;
    };
    return (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2' onClick={(e) => verifyClick(e, closeModal)}>
            <div className='bg-white dark:bg-gray-800 rounded-3xl shadow-vox-2xl max-w-[95vw] w-full mx-2 max-h-[95vh] overflow-hidden flex flex-col'>
                <div className='bg-gradient-to-r from-vox-blue-600 to-vox-blue-800 px-6 py-6'>
                    <div className='flex items-center justify-between'>
                        <div className="w-64">
                            <label className="block text-xs text-white/80 mb-1">Competência (segue o disparo)</label>
                            <div className="flex gap-2">
                                <input
                                    type="month"
                                    value={competencia.slice(0, 7)}
                                    onChange={(e) => e.target.value && setCompetencia(`${e.target.value}-01`)}
                                    className="border rounded-lg p-2 w-full text-gray-800"
                                />
                                <button
                                    type="button"
                                    onClick={sincronizarSpxo}
                                    disabled={sincronizandoSpxo}
                                    title="Busca no SPXO (SaperX) linhas e minutos só dos circuitos deste cliente (CNPJ) e atualiza os itens com fonte SPXO"
                                    className="shrink-0 px-3 py-2 rounded-lg text-xs font-semibold bg-white/15 text-white border border-white/40 hover:bg-white/25 disabled:opacity-50 whitespace-nowrap"
                                >
                                    {sincronizandoSpxo ? 'Sincronizando…' : '⚡ Sincronizar SPXO'}
                                </button>
                            </div>
                        </div>
                        <h1 className='text-2xl font-bold text-white absolute left-1/2 transform -translate-x-1/2'>
                            Itens - {clienteSelecionado && clienteSelecionado.nome}
                        </h1>
                        <div className="w-64">
                            {contratoSelecionado && (
                                <select
                                    value={contratoSelecionado.id}
                                    onChange={(e) => setContratoSelecionado(contratos.find((c) => String(c.id) === e.target.value))}
                                    className="border rounded-lg p-2 w-full text-gray-800"
                                >
                                    {contratos.map((c) => (
                                        <option key={c.id} value={c.id}>{c.nome}{c.status !== 'ativo' ? ` (${rotuloStatusContrato(c.status)})` : ''}</option>
                                    ))}
                                </select>
                            )}
                        </div>
                    </div>
                </div>
                <div className='flex-1 overflow-y-auto p-4'>
                    {!contratosProntos && !contratoSelecionado ? (
                        <LoadingSpinner />
                    ) : contratos.length === 0 ? (
                        <p className="p-6 text-center text-gray-500">Cliente sem contratos cadastrados.</p>
                    ) : !contratoSelecionado ? (
                        <SeletorContrato contratos={contratos} onSelecionar={setContratoSelecionado} />
                    ) : loading ? (
                        <LoadingSpinner />
                    ) : (
                        <DataTable
                            data={buildTableData()}
                            noSort={true}
                            headers={columns}
                            observacoes={clienteSelecionado?.observacoes}
                            onSelectionChange={(selectedItems) => console.log(selectedItems)}
                            exportButton={true}
                            docTitle={`Itens - ${clienteSelecionado?.nome || "Cliente"}`}
                            hasMonthSelection={getMonthName(month)}
                            footerValues={generateFooterValues()}
                            fontHeader={12}
                            fontData={11}
                            fixedSize={false}
                        />
                    )}
                </div>
                {/*FOOTER COM VALORES TOTAIS*/}
                {contratoSelecionado && (
                    <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900">
                        <div className="flex flex-col space-y-4">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">
                                Resumo Financeiro
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="bg-white dark:bg-gray-700 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-600">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                            Total Implantado
                                        </span>
                                        <span className="text-lg font-bold text-green-600 dark:text-green-400">
                                            {fmtBRL(totalMonth)}
                                        </span>
                                    </div>
                                </div>
                                <div className="bg-white dark:bg-gray-700 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-600">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                            Total Contratado
                                        </span>
                                        <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
                                            {fmtBRL(total)}
                                        </span>
                                    </div>
                                </div>
                                {uniqueTotal > 0 && (
                                    <div className="bg-white dark:bg-gray-700 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-600">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                                Cobrança Única
                                            </span>
                                            <span className="text-lg font-bold text-orange-600 dark:text-orange-400">
                                                {fmtBRL(uniqueTotal)}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                    <BaseButton
                        onClick={closeModal}
                        variant="primary"
                        size="md"
                    >
                        Fechar
                    </BaseButton>
                </div>
            </div>
        </div>
    );
};

export default Itens;